import { useState, useEffect } from 'react'
import { PlusIcon, XMarkIcon, CalculatorIcon, ExclamationTriangleIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import SortableHeader, { useSort } from '../../components/SortableHeader'

interface Unit {
  id: number
  name: string
  type: 'mass' | 'volume' | 'count'
  to_g_factor: number | null
  is_base: boolean
  can_convert_to_grams?: boolean
  created_at: string
  updated_at: string
}

interface Density {
  id: number
  ingredient_id: number
  ingredient_name: string
  density_g_per_ml: number
  created_at: string
  updated_at: string
}

const API_BASE_URL = '/api/v1/dev'

export default function Units() {
  const [units, setUnits] = useState<Unit[]>([])
  const [densities, setDensities] = useState<Density[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showDensitiesModal, setShowDensitiesModal] = useState(false)
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'mass' as 'mass' | 'volume' | 'count',
    to_g_factor: '',
    is_base: false
  })

  // Debug: Verify this is the correct component
  console.log('🔥 UNITS COMPONENT LOADED - CONNECTED TO API', new Date().toISOString())

  // Fetch units from API
  const fetchUnits = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('🔄 FETCHING UNITS FROM API:', `${API_BASE_URL}/units`)
      const response = await fetch(`${API_BASE_URL}/units`)
      const data = await response.json()
      console.log('📥 API RESPONSE:', data)

      if (response.ok) {
        setUnits(data.data)
        console.log('✅ UNITS LOADED:', data.data.length, 'items')
      } else {
        setError('Error al cargar unidades')
        console.error('❌ API ERROR:', data)
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('❌ FETCH ERROR:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch densities from API
  const fetchDensities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/densities`)
      const data = await response.json()
      if (response.ok) {
        setDensities(data.data)
      }
    } catch (err) {
      console.error('Error fetching densities:', err)
    }
  }

  useEffect(() => {
    fetchUnits()
    fetchDensities()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      let response
      const body = {
        name: formData.name,
        type: formData.type,
        to_g_factor: formData.type === 'mass' && formData.to_g_factor ? parseFloat(formData.to_g_factor) : null,
        is_base: formData.is_base
      }

      if (editingUnit) {
        // Update unit
        response = await fetch(`${API_BASE_URL}/units/${editingUnit.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
      } else {
        // Create new unit
        response = await fetch(`${API_BASE_URL}/units`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
      }

      const data = await response.json()

      if (response.ok) {
        // Refresh the units list
        await fetchUnits()
        closeModal()
      } else {
        if (data.errors) {
          // Display validation errors
          const errorMessages = Object.values(data.errors).flat().join(', ')
          setError(errorMessages)
        } else {
          setError(data.message || 'Error al guardar unidad')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving unit:', err)
    } finally {
      setSaving(false)
    }
  }

  const openModal = (unit?: Unit) => {
    if (unit) {
      setEditingUnit(unit)
      setFormData({
        name: unit.name,
        type: unit.type,
        to_g_factor: unit.to_g_factor ? unit.to_g_factor.toString() : '',
        is_base: unit.is_base
      })
    } else {
      setEditingUnit(null)
      setFormData({ name: '', type: 'mass', to_g_factor: '', is_base: false })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingUnit(null)
    setFormData({ name: '', type: 'mass', to_g_factor: '', is_base: false })
    setError(null)
  }

  const deleteUnit = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar esta unidad?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/units/${id}`, {
        method: 'DELETE',
      })

      const data = await response.json()

      if (response.ok) {
        // Refresh the units list
        await fetchUnits()
      } else {
        setError(data.message || 'Error al eliminar unidad')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting unit:', err)
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'mass':
        return 'bg-blue-100 text-blue-800'
      case 'volume':
        return 'bg-green-100 text-green-800'
      case 'count':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeText = (type: string) => {
    switch (type) {
      case 'mass':
        return 'Masa'
      case 'volume':
        return 'Volumen'
      case 'count':
        return 'Unidades'
      default:
        return 'Desconocido'
    }
  }

  const { sortField, sortDirection, handleSort, sortData } = useSort<Unit>('name')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Unidades</h1>
            <p className="mt-1 text-sm text-gray-600">
              Sistema de unidades con conversión a gramos como unidad base
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
            <button
              type="button"
              onClick={() => setShowDensitiesModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              🌡️ Ver Densidades
            </button>
            <button
              type="button"
              onClick={() => openModal()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Nueva Unidad
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Cerrar</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalculatorIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Unidades de Masa</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {units.filter(u => u.type === 'mass').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold text-sm">V</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Unidades de Volumen</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {units.filter(u => u.type === 'volume').length}
                  </dd>
                  <dd className="text-xs text-gray-500">
                    {densities.length} densidades definidas
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold text-sm">#</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Unidades Discretas</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {units.filter(u => u.type === 'count').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert about conversions */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <CalculatorIcon className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Sistema de conversión:</strong> El gramo (g) es la unidad base.
              Las unidades de volumen requieren densidad del ingrediente para conversión.
              Las unidades discretas requieren peso medio por ingrediente.
            </p>
          </div>
        </div>
      </div>

      {/* Units Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Unidades Registradas ({units.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Nombre" field="name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Tipo" field="type" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Factor a Gramos" field="to_g_factor" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Es Base
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversión
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortData(units).map((unit) => (
                <tr key={unit.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{unit.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(unit.type)}`}>
                      {getTypeText(unit.type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {unit.to_g_factor !== null ? parseFloat(String(unit.to_g_factor)).toFixed(2) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {unit.is_base ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        BASE
                      </span>
                    ) : (
                      <span className="text-gray-400">No</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {unit.type === 'volume' ? (
                      <div className="text-sm">
                        <div className="text-blue-600 font-medium">Requiere densidad</div>
                        <div className="text-gray-500 text-xs">
                          {densities.length} ingredientes con densidad
                        </div>
                      </div>
                    ) : unit.can_convert_to_grams ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        SÍ
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        NO
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(unit)}
                        className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={unit.is_base}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteUnit(unit.id)}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={unit.is_base}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-11/12 max-w-4xl">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingUnit ? 'Editar Unidad' : 'Nueva Unidad'}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nombre de la Unidad
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="ej: kilogramo, litro, docena"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Tipo de Unidad
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'mass' | 'volume' | 'count' })}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                      >
                        <option value="mass">Masa</option>
                        <option value="volume">Volumen</option>
                        <option value="count">Unidades Discretas</option>
                      </select>
                    </div>
                    {formData.type === 'mass' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Factor de Conversión a Gramos
                        </label>
                        <input
                          type="number"
                          step="any"
                          value={formData.to_g_factor}
                          onChange={(e) => setFormData({ ...formData, to_g_factor: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="ej: 1000 para kg"
                          required={formData.type === 'mass'}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          1 {formData.name || 'unidad'} = {formData.to_g_factor || '?'} gramos
                        </p>
                      </div>
                    )}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_base"
                        checked={formData.is_base}
                        onChange={(e) => setFormData({ ...formData, is_base: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_base" className="ml-2 block text-sm text-gray-900">
                        Es unidad base del sistema
                      </label>
                    </div>
                    {formData.type !== 'mass' && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                        <p className="text-sm text-blue-700">
                          Las unidades de {formData.type === 'volume' ? 'volumen' : 'conteo'}
                          requieren información adicional del ingrediente para conversión a gramos.
                        </p>
                      </div>
                    )}

                    {/* Error display in modal */}
                    {error && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Guardando...
                      </div>
                    ) : (
                      editingUnit ? 'Actualizar' : 'Crear'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Densities Modal */}
      {showDensitiesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Densidades para Unidades de Volumen
              </h3>
              <button
                onClick={() => setShowDensitiesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CalculatorIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Información sobre densidades:</strong> Las unidades de volumen (ml, l) requieren
                      densidades específicas por ingrediente para poder convertirse a gramos. Sin densidad,
                      no es posible realizar conversiones de volumen a masa.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {densities.length > 0 ? (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h4 className="text-md font-medium text-gray-900">
                    Densidades Definidas ({densities.length})
                  </h4>
                </div>
                <div className="overflow-x-auto max-h-96">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ingrediente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Densidad (g/ml)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conversión Ejemplo
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {densities.map((density) => (
                        <tr key={density.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {density.ingredient_name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {parseFloat(density.density_g_per_ml.toString()).toFixed(3)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              100ml → {(100 * parseFloat(density.density_g_per_ml.toString())).toFixed(1)}g
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-500">
                  <div className="mb-4">
                    <span className="text-4xl">🌡️</span>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No hay densidades definidas
                  </h3>
                  <p className="text-sm text-gray-500">
                    Las densidades se definen en la sección de "Densidades" para permitir
                    conversiones de unidades de volumen a masa.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDensitiesModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}